import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AppsService } from '../../../../data-services/apps.service';
import { SalesOrdersService } from '../../../../data-services/sales-orders.service';
import { AutomationErrorHandlerService } from '../../../services';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class ActivateProductService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private translateService: TranslateService,
    private sanitizer: DomSanitizer,
    private appsService: AppsService,
    private salesOrdersService: SalesOrdersService,
    protected readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  handleActionError(): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ACTIVATE_PRODUCT.ACTION_LOADING_FAILED');
  }

  handleActivityError(): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ACTIVATE_PRODUCT.ACTIVITY_LOADING_FAILED');
  }

  getActivitySubtitle(_namespace: string, _activity: ActivityInterface): Observable<string> {
    return of('');
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    const inputData = activity?.inputData ? JSON.parse(activity?.inputData) : {};

    const accountGroupId = inputData.account_group_id;
    const orderId = activityData.order_id;

    if (!orderId) {
      return this.handleActivityError();
    }

    const data = activity?.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    const lineItems = data.line_items || [];
    if (lineItems.length > 0) {
      const firstLineItem = lineItems.length > 0 ? lineItems[0] : null;
      const appId = firstLineItem.identifier?.app_key?.app_id || '';
      const editionId = firstLineItem.identifier?.app_key?.edition_id;
      if (!appId) {
        return this.handleActivityError();
      }

      return this.appsService
        .getProductName(
          {
            appId,
            editionId,
          },
          false,
          true,
        )
        .pipe(
          switchMap((productName) => {
            let plusOthers = '';
            if (lineItems.length > 1) {
              const otherCount = lineItems.length - 1;
              const otherKey =
                otherCount === 1 ? 'AUTOMATIONS.EDITOR.FILTERS.OTHER' : 'AUTOMATIONS.EDITOR.FILTERS.OTHERS';
              const other = this.translateService.instant(otherKey);
              plusOthers = ` (+${otherCount} ${other})`;
            }
            const orderLink = this.salesOrdersService.getOrderLink(orderId, accountGroupId);
            return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ACTIVATE_PRODUCT.ACTIVITY_TEXT', {
              productName: productName,
              plusOthers: plusOthers,
              orderLink: orderLink,
            });
          }),
          catchError((err) => {
            console.error(err);
            return this.handleActivityError();
          }),
        );
    }

    return this.handleActivityError();
  }

  getTemplateActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};
    const lineItems = data.line_items || [];

    if (lineItems.length > 0) {
      const firstLineItem = lineItems.length > 0 ? lineItems[0] : null;
      const appId = firstLineItem.identifier?.app_key?.app_id || '';
      const editionId = firstLineItem.identifier?.app_key?.edition_id;
      if (!appId) {
        return this.getActionSubtitleEmptyState(step);
      }

      return this.appsService
        .getProductName(
          {
            appId,
            editionId,
          },
          false,
          true,
        )
        .pipe(
          switchMap((productName) => {
            let plusOthers = '';
            if (lineItems.length > 1) {
              const otherCount = lineItems.length - 1;
              const otherKey =
                otherCount === 1 ? 'AUTOMATIONS.EDITOR.FILTERS.OTHER' : 'AUTOMATIONS.EDITOR.FILTERS.OTHERS';
              const other = this.translateService.instant(otherKey);
              plusOthers = ` (+${otherCount} ${other})`;
            }
            return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ACTIVATE_PRODUCT.ACTION', {
              productName: productName,
              plusOthers: plusOthers,
            });
          }),
          catchError((err) => {
            console.error(err);
            return this.getActionSubtitleErrorState({}, step);
          }),
        );
    }
    this.errorHandler.setValidationError({
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.ACTIVATE_PRODUCT.CONFIG_ERROR'),
      isNonDismissable: true,
    });
    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitleSuccessState(_data: any): Observable<string> {
    return of('');
  }

  getActionSubtitleErrorState(_data: any, _step: WorkflowStepInterface): Observable<string> {
    return this.handleActionError();
  }

  getActionSubtitleEmptyState(_step: WorkflowStepInterface): Observable<string> {
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.ACTIVATE_PRODUCT.ACTIVATE_PRODUCT');
  }
}
